import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService from "../services/ApiService";
import CouponInput from "../components/CouponInput";
import logo from "../assets/logo.png";
import "./PurchasePlan.css";

const PurchasePlan = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pricingPlans, setPricingPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Get selected car type from navigation state
  const selectedCarType = location.state?.selectedCarType;
  const fromPurchaseFlow = location.state?.fromPurchaseFlow;
  useEffect(() => {
    // If no car type selected and coming from purchase flow, redirect to car selection
    if (fromPurchaseFlow && !selectedCarType) {
      navigate("/select-car-type");
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await ApiService.getPricingPlans();

        // Filter plans that are available for the selected car type
        let filteredPlans = response.data || [];
        if (selectedCarType) {
          filteredPlans = filteredPlans.filter((plan) =>
            plan.pricingPlanCarTypes?.some(
              (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
            )
          );
        }

        setPricingPlans(filteredPlans);
      } catch (error) {
        console.error("Error fetching pricing plans:", error);
        setError("Failed to load pricing plans. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCarType, fromPurchaseFlow, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  // Coupon handlers
  const handleCouponApply = (couponData) => {
    setAppliedCoupon(couponData);
  };

  const handleCouponRemove = () => {
    setAppliedCoupon(null);
  };

  const handlePurchase = async () => {
    if (!selectedPlan || !selectedCarType) {
      setError("Please select a plan and car type");
      return;
    }

    try {
      setPurchasing(true);
      setError("");

      // Create purchase with optional coupon code
      const purchaseData = {
        pricingPlanId: selectedPlan.id,
        carTypeId: selectedCarType.id,
        paymentMethod: "mock_payment", // This would be replaced with actual payment method
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
      };
      const purchaseResponse = await ApiService.createPurchase(purchaseData);
      const purchase = purchaseResponse.data.purchase;

      // Check if this is a zero-amount purchase (completed with coupon)
      if (purchase.paymentStatus === "completed") {
        // Purchase was completed for free with coupon - redirect to dashboard
        navigate("/dashboard");
        return;
      }

      // Mock payment completion (in real app, this would integrate with payment gateway)
      const completionData = {
        paymentStatus: "completed",
        transactionId: `mock_${Date.now()}`,
      };

      await ApiService.completePurchase(purchase.id, completionData);

      // Check if plan has infinite validity (-1) to determine next step
      if (selectedPlan.validity === -1) {
        // Infinite validity plan - go directly to booking calendar
        navigate("/dashboard");
      } else {
        // Subscription plan - go to dashboard
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Purchase error:", error);
      setError(error.message || "Purchase failed. Please try again.");
    } finally {
      setPurchasing(false);
    }
  };
  const getPlanPrice = (plan) => {
    if (!selectedCarType) return "N/A";

    const pricingInfo = plan.pricingPlanCarTypes?.find(
      (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
    );

    if (!pricingInfo) return "N/A";

    const originalPrice = parseFloat(pricingInfo.price);

    if (appliedCoupon) {
      const discountAmount = parseFloat(appliedCoupon.discountAmount);
      const finalPrice = Math.max(0, originalPrice - discountAmount);
      return `₹${finalPrice.toFixed(2)}`;
    }

    return `₹${originalPrice.toFixed(2)}`;
  };

  const getOriginalPrice = (plan) => {
    if (!selectedCarType) return 0;

    const pricingInfo = plan.pricingPlanCarTypes?.find(
      (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
    );

    return pricingInfo ? parseFloat(pricingInfo.price) : 0;
  };

  const handleGoBack = () => {
    if (fromPurchaseFlow) {
      navigate("/select-car-type");
    } else {
      navigate("/dashboard");
    }
  };

  if (loading) {
    return (
      <div className="purchase-plan">
        <header className="dashboard-header">
          <div className="header-content">
            <img src={logo} alt="Steam & Drive" className="dashboard-logo" />
            <div className="user-info">
              <span>
                Welcome, {currentUser?.displayName || currentUser?.email}
              </span>
              <button onClick={handleLogout} className="logout-btn">
                Logout
              </button>
            </div>
          </div>
        </header>
        <div className="dashboard-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading pricing plans...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-plan">
      <header className="dashboard-header">
        <div className="header-content">
          <img src={logo} alt="Steam & Drive" className="dashboard-logo" />
          <div className="user-info">
            <span>
              Welcome, {currentUser?.displayName || currentUser?.email}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        <div className="dashboard-content">
          <div className="purchase-header">
            <button onClick={handleGoBack} className="back-button">
              ← Back
            </button>
            <h2>Choose Your Plan</h2>
            {selectedCarType && (
              <p className="selected-car-info">
                Selected vehicle: <strong>{selectedCarType.name}</strong>
              </p>
            )}
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="pricing-plans-grid">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`pricing-plan-card ${
                  selectedPlan?.id === plan.id ? "selected" : ""
                }`}
                onClick={() => handleSelectPlan(plan)}
              >
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <div className="plan-price">{getPlanPrice(plan)}</div>
                </div>

                <div className="plan-features">
                  <div className="feature">
                    <span className="feature-label">Credits:</span>
                    <span className="feature-value">{plan.creditsGiven}</span>
                  </div>

                  {plan.oneTimeCreditsGiven > 0 && (
                    <div className="feature">
                      <span className="feature-label">Bonus Credits:</span>
                      <span className="feature-value">
                        {plan.oneTimeCreditsGiven}
                      </span>
                    </div>
                  )}

                  <div className="feature">
                    <span className="feature-label">Validity:</span>
                    <span className="feature-value">
                      {plan.validity === -1
                        ? "Unlimited"
                        : `${plan.validity} days`}
                    </span>
                  </div>
                </div>

                {plan.description && (
                  <div className="plan-description">
                    <p>{plan.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {pricingPlans.length === 0 && (
            <div className="no-plans">
              <p>No pricing plans available for the selected car type.</p>
              <p>
                Please try selecting a different car type or contact support.
              </p>
            </div>
          )}

          {selectedPlan && (
            <div className="purchase-summary">
              <div className="summary-content">
                <h4>Purchase Summary</h4>
                <div className="summary-details">
                  <div className="summary-row">
                    <span>Plan:</span>
                    <span>{selectedPlan.name}</span>
                  </div>
                  <div className="summary-row">
                    <span>Car Type:</span>
                    <span>{selectedCarType?.name}</span>
                  </div>
                  <div className="summary-row">
                    <span>Credits:</span>
                    <span>{selectedPlan.creditsGiven}</span>
                  </div>
                  {selectedPlan.oneTimeCreditsGiven > 0 && (
                    <div className="summary-row">
                      <span>Bonus Credits:</span>
                      <span>{selectedPlan.oneTimeCreditsGiven}</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>{getPlanPrice(selectedPlan)}</span>{" "}
                  </div>
                </div>

                {/* Coupon Input */}
                <CouponInput
                  onCouponApply={handleCouponApply}
                  onCouponRemove={handleCouponRemove}
                  appliedCoupon={appliedCoupon}
                  originalAmount={getOriginalPrice(selectedPlan)}
                />

                <button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="purchase-button"
                >
                  {purchasing ? "Processing..." : "Purchase Plan"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchasePlan;
